import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild
} from '@angular/core';
import {Clipboard} from '@angular/cdk/clipboard';
import {OpenTablesStore} from '../data-access/open-tables.store';
import {OpenTablesListComponent, OpenTablesSort} from './open-tables-list.component';
import {OpenTablesMapComponent} from './open-tables-map.component';
import {SpendingLimitDialogComponent} from './spending-limit-dialog.component';
import {fmtClock, fmtEur} from '../shared/idle-warning-levels';
import {OpenTable} from '../shared/open-tables.model';
import {CookieService} from '../../../_services/cookie.service';
import {CookieType} from '../../../x-models/x-enums/cookie-type';
import {ResizeHandleComponent} from '../../../_components/resize-handle/resize-handle.component';
import {clampPanelWidth, readStoredPanelWidth} from '../../../_components/resize-handle/panel-width';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {Roles} from '../../../_auth/x-enums/roles';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';

/** The three view modes. `split` only exists at {@link LARGE_BREAKPOINT_PX} and above. */
export type OpenTablesView = 'split' | 'map' | 'list';

/** Viewport width, in pixels, from which the split view is offered (the handover's §1). */
export const LARGE_BREAKPOINT_PX = 900;

/**
 * Narrowest the split view's list rail may be dragged, in pixels.
 *
 * **Measured, not chosen.** The rail was swept across the live shard's own cards in 4 px steps, checking at each
 * width which text run still occupies two visual lines and whether any card overflows its own box. Reading the
 * thresholds from the bottom up: the card head stops overflowing at 284 px, "letzte Buchung HH:MM" straightens
 * out at 284 px, "Bereich · N Plätze" at 324 px, and the last offender — the `AD HOC` badge splitting into
 * "AD / HOC" — clears at **344 px**. 360 ships that with a small cushion and is a round number.
 *
 * Two things make 344 a *conservative* floor rather than a tight one: it was measured against the widest timer
 * pill the live data produces (a stale reference reading `12943 Std 3 Min` — a real venue's `1 Std 28 Min` is
 * ~35 px narrower and frees the badge much sooner), and the card head no longer carries the "Tisch NNNN" label
 * at all.
 *
 * The old rail was `flex: 1 1 0` clamped to 340–430 px, and in the Operator panel a 1440 px window left it at
 * exactly its 340 px floor — inside the wrapping zone, which is what produced the broken "Tisch / 801",
 * "AD / HOC" and three-line "Ohne / Grundriss- / Position" cards (8 of 10 wrapped) this width prevents.
 * See docs/articles/booking/open-tables-overview.md §6.
 *
 * **Raised from 360 to 390 by #204.** The per-card kebab is drawn over the card's top-right corner, so the card
 * head now reserves a 26 px lane for it (`.otl-card-head`'s `padding-right`) — 26 px that the badge/timer/meta
 * measurements above no longer have. Carrying the same arithmetic through: the last offender clears at
 * 344 + 26 = 370, and 390 ships that with the same cushion the original 360 gave 344. Leaving it at 360 would
 * have put the widest timer pill back inside the wrapping zone the constant exists to stay out of.
 */
export const LIST_MIN_WIDTH_PX = 390;

/** Widest the list rail may be dragged. A hard ceiling — the split view is a map with a list, not the reverse. */
export const LIST_MAX_WIDTH_PX = 720;

/**
 * How much of the split the map keeps whatever the list does. The effective maximum is derived from the
 * *measured* content width minus this, so the list can never swallow the map on a smaller desktop (or when the
 * Operator panel's own rail is widened).
 */
export const MIN_MAP_WIDTH_PX = 360;

const VIEW_STORAGE_KEY = 'openTables.view';
const SORT_STORAGE_KEY = 'openTables.sort';
const FREE_TABLES_STORAGE_KEY = 'openTables.showFree';
const LIST_WIDTH_STORAGE_KEY = 'openTables.listWidth';

/**
 * **Offene Tische** — the live open-tables overview (#205).
 *
 * A near-literal migration of the `Offene Tische` design handover: a header bar (title, open count, open total,
 * clock, attention pill, view toggle) over a content row that is a **map ¾ + list ¼** split on wide screens, and
 * either half alone otherwise. Everything renders from three merged sources held by {@link OpenTablesStore} — the
 * live SignalR reference feed, the floor-plan geometry, and the shard-effective device configuration that supplies
 * the idle-warning thresholds *and* their colours.
 *
 * **Shell-agnostic on purpose.** The root is `position: absolute; inset: 0`, filling whatever routed region it is
 * given, and it renders no navigation of its own — so the same component serves the admin panel today and the
 * standalone Operator panel (workstream D) without a second implementation.
 *
 * The values in the template are the handover's own (see `floor-plan-editor.scss`, whose shared `.cq-btn` chrome
 * and `line-height: normal` reset this screen also relies on) rather than mapped onto this app's utility scale,
 * for the same reason the Tischplan editor's are: it has to be indistinguishable from the reference side by side.
 */
@Component({
  selector: 'app-open-tables',
  standalone: true,
  imports: [
    OpenTablesMapComponent, OpenTablesListComponent, ResizeHandleComponent, SpendingLimitDialogComponent
  ],
  providers: [OpenTablesStore],
  styleUrl: '../../table-layout/manager/floor-plan-editor.scss',
  styles: [`
    /* Fills the whole routed area (the host shell already reserves the space under its own chrome) — this app's
       equivalent of the handover's fixed, inset-0 root. */
    .ot-root {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      background: #e7e5e1;
      color: #1c1c1e;
      font-family: 'Rubik', system-ui, sans-serif;
      -webkit-font-smoothing: antialiased;
      overflow: hidden;
    }

    .ot-bar {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
      padding: 13px 20px;
      background: #fff;
      border-bottom: 1px solid #e2e0dc;
      z-index: 20;
    }

    .ot-brand {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .ot-brand-mark {
      width: 38px;
      height: 38px;
      border-radius: 11px;
      background: #c50e6c;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }

    .ot-brand-title {
      font-size: 18px;
      font-weight: 700;
      line-height: 1.1;
      margin: 0;
    }

    .ot-brand-sub {
      font-size: 13px;
      /* The handover's #8a8a8e is 3.4:1 on white — see OpenTablesListComponent for the same one-step darkening. */
      color: #6b6b70;
      line-height: 1.1;
      margin-top: 2px;
      font-variant-numeric: tabular-nums;
    }

    .ot-spacer {
      flex: 1 1 20px;
    }

    .ot-attention {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 9px 15px;
      border-radius: 999px;
      font-size: 13.5px;
      font-weight: 600;
      background: #fdf6e3;
      color: #8a6d12;
    }

    .ot-group {
      display: flex;
      align-items: center;
      gap: 5px;
      background: #f0efed;
      border-radius: 12px;
      padding: 5px;
    }

    .ot-chip {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 8px 16px;
      border: 0;
      border-radius: 8px;
      font: inherit;
      font-size: 13.5px;
      font-weight: 600;
      background: transparent;
      color: #6b6b70;
      cursor: pointer;
      transition: background .12s, color .12s, box-shadow .12s, transform .06s;
    }

    .ot-chip:active {
      transform: translateY(1px);
    }

    .ot-chip[aria-pressed='true'] {
      background: #fff;
      color: #c50e6c;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
    }

    /* The one addition to the handover's header (the geometry refresh). It sits in its own track, in the TITLE
       row, so the reference's second row — attention pill + view toggle — keeps its geometry AND its width
       budget exactly (see the template comment for the 390 px arithmetic). */
    .ot-tools {
      display: flex;
      align-items: center;
      gap: 5px;
      background: #f0efed;
      border-radius: 12px;
      padding: 5px;
    }

    .ot-icon-btn {
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: #6b6b70;
      cursor: pointer;
      transition: background .12s, color .12s, transform .06s;
    }

    .ot-icon-btn:active {
      transform: translateY(1px);
    }

    .ot-icon-btn[aria-pressed='true'] {
      background: #fff;
      color: #c50e6c;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
    }

    .ot-content {
      flex: 1;
      min-height: 0;
      display: flex;
    }

    /* Split: the list is a rail beside the map, whose width the user drags (and which is remembered). The
       divider line is drawn by the resize handle immediately to its left, so this side carries no border. */
    .ot-list-split {
      flex: 0 0 auto;
      background: #fff;
      display: flex;
      flex-direction: column;
    }

    /* Full width: the list owns the screen and lays its cards out as a grid on its own tinted canvas. */
    .ot-list-full {
      flex: 1 1 0;
      min-width: 0;
      background: #eef0f0;
      display: flex;
      flex-direction: column;
    }

    .ot-banner {
      flex: 0 0 auto;
      padding: 8px 20px;
      background: #fdecec;
      color: #a8321f;
      font-size: 13px;
      font-weight: 600;
    }
  `],
  template: `
    <div class="ot-root">
      <div class="ot-bar">
        <div class="ot-brand">
          <span class="ot-brand-mark" aria-hidden="true">
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h13M3 12h13M3 18h9"/>
              <circle cx="20" cy="7.5" r="1.4" fill="#fff" stroke="none"/>
              <circle cx="20" cy="16.5" r="1.4" fill="#fff" stroke="none"/>
            </svg>
          </span>
          <div>
            <h1 class="ot-brand-title">Offene Tische</h1>
            <div class="ot-brand-sub">{{ headline() }}</div>
          </div>
        </div>

        <!--
          The one control the handover does not have, and it sits in the TITLE row rather than beside the view
          toggle. Icon-only on purpose, and placed here for a measured reason: at 390 px the header's content box
          is 350 px and the reference's second row already fills 349 of it (pill 185 + gap 16 + toggle 148), so
          ANY additional control on that row forces a third header row — even at zero gap. Riding the title row
          instead (brand 240 + gap 16 + tool 44 = 300) restores the reference's two-row phone header. See
          docs/articles/booking/open-tables-overview.md §8.3.
          (The free-tables toggle used to sit beside it; it is a map LAYER control and now lives in the canvas's
          camera cluster — see OpenTablesMapComponent.)
        -->
        <div class="ot-tools">
          <button type="button" class="ot-icon-btn" (click)="refresh()"
                  aria-label="Grundriss und Einstellungen neu laden" title="Grundriss neu laden">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M20 11a8 8 0 1 0-2.3 5.7"/>
              <path d="M20 4v7h-7"/>
            </svg>
          </button>
        </div>

        <div class="ot-spacer"></div>

        @if (store.attentionCount(); as attention) {
          <span class="ot-attention" role="status">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 7v5l3 2"/>
            </svg>Aufmerksamkeit · {{ attention }}
          </span>
        }

        <div class="ot-group" role="group" aria-label="Ansicht">
          @for (chip of viewChips(); track chip.key) {
            <button type="button" class="ot-chip" [attr.aria-pressed]="view() === chip.key"
                    (click)="setView(chip.key)">{{ chip.label }}
            </button>
          }
        </div>
      </div>

      @if (store.feedFailed()) {
        <p class="ot-banner" role="alert">
          Die Live-Verbindung ist unterbrochen — die Ansicht zeigt den zuletzt bekannten Stand.
        </p>
      }

      <div #content class="ot-content">
        @if (showMap()) {
          <app-open-tables-map
            [style.flex]="'1 1 0'"
            [tables]="store.openTables()"
            [freeTables]="store.freeTables()"
            [areas]="store.floorAreas()"
            [background]="store.background()"
            [levels]="store.warningLevels()"
            [showFreeTables]="showFreeTables()"
            [selectedId]="selectedId()"
            (selected)="onMapSelected($event)"
            (freeTablesToggled)="toggleFreeTables()"
            (selectionCleared)="clearSelection()"/>
        }

        <!--
          The splitter exists ONLY in the split view, which by construction only exists at
          LARGE_BREAKPOINT_PX and above — so there is no width at which a phone user can reach it, and no
          media query needed to keep it out of the tab order.
        -->
        @if (split()) {
          <app-resize-handle edge="start" label="Breite der Tischliste ändern"
                             [width]="listWidth()" (widthChange)="requestedListWidth.set($event)"
                             [min]="listMinWidth" [max]="listMaxWidth()"
                             (committed)="persistListWidth($event)"/>
        }

        @if (showList()) {
          <div [class]="split() ? 'ot-list-split' : 'ot-list-full'"
               [style.width.px]="split() ? listWidth() : null">
            <app-open-tables-list
              [tables]="store.openTables()"
              [selectedId]="selectedId()"
              [sort]="sort()"
              [layout]="split() ? 'column' : 'grid'"
              [emptyText]="emptyText()"
              [canChangeLimit]="canChangeLimit"
              (sortChanged)="setSort($event)"
              (selected)="toggleSelection($event)"
              (copyRequested)="copyReferenceId($event)"
              (limitRequested)="openLimitDialog($event)"/>
          </div>
        }
      </div>
    </div>

    <!--
      Outside .ot-root: that element is \`overflow: hidden\`, and a modal that can be clipped by the screen it
      belongs to is not a modal. It shares the same positioned ancestor, so \`inset: 0\` still covers exactly the
      routed region. Recreated on every open, which is what re-seeds the box from the current limit.
    -->
    @if (limitTarget(); as target) {
      <app-spending-limit-dialog [referenceId]="target.id" [tableName]="target.number"
                                 [currentLimit]="target.spendingLimit"
                                 (saved)="onLimitSaved($event)" (cancelled)="limitTarget.set(null)"/>
    }
  `
})
export class OpenTablesComponent {
  protected readonly store = inject(OpenTablesStore);
  private readonly cookies = inject(CookieService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alerts = inject(GlobalAlertService);
  /**
   * The CDK's clipboard, not the app's usual `copy-to-clipboard` package.
   *
   * Two reasons, and the second is the load-bearing one: it is **injectable**, so a spec can assert the copy
   * without touching the real clipboard — and the package's fallback path opens a blocking `window.prompt`,
   * which in headless Chrome hangs the whole Karma run rather than failing a test.
   */
  private readonly clipboard = inject(Clipboard);

  /**
   * Whether this session may change a spending limit (#204) — `ShardAdmin`/`CompanyOwner`/`PlatformOwner`.
   *
   * Read once, from the session that rendered the screen: a role change means a new token and a new session, so
   * there is nothing here to keep re-evaluating.
   *
   * **`Operator` is deliberately absent.** It is a standalone read-only baseline role (held *instead* of
   * ShardAdmin, #205) that reaches this screen through its own route entry — so it can neither be assumed to
   * imply the others nor be caught by their absence. It sees the kebab and "ID kopieren", and no write.
   *
   * Presentation only: the real gate is `v2025_4.ReferenceController.SetSpendingLimit`, which authorizes the
   * request regardless of what this decides to draw.
   */
  protected readonly canChangeLimit: boolean;

  /** The card whose limit is being edited, or `null`. Non-null is what renders the dialog. */
  protected readonly limitTarget = signal<OpenTable | null>(null);

  private readonly list = viewChild(OpenTablesListComponent);
  private readonly content = viewChild<ElementRef<HTMLElement>>('content');

  /** Viewport width, tracked so the split view can fall back when the window narrows. */
  private readonly viewportWidth = signal(typeof window === 'undefined' ? 1200 : window.innerWidth);

  /**
   * Measured width of the split row, which is **not** the viewport: both hosts of this screen (the admin panel
   * and the Operator panel) put chrome beside it, and the Operator rail is itself resizable. Observing the
   * element is what lets the list's ceiling react to that instead of guessing an allowance.
   */
  private readonly contentWidth = signal(0);

  protected readonly listMinWidth = LIST_MIN_WIDTH_PX;

  /**
   * The width the user asked for, which survives the window being temporarily too narrow to honour it — the
   * same non-destructive treatment {@link chosenView} gets. {@link listWidth} is what actually renders.
   */
  protected readonly requestedListWidth = signal(
    readStoredPanelWidth(LIST_WIDTH_STORAGE_KEY, LIST_MIN_WIDTH_PX, LIST_MIN_WIDTH_PX, LIST_MAX_WIDTH_PX));

  /** The user's chosen view. May be `split` even while narrow — {@link view} resolves that. */
  private readonly chosenView = signal<OpenTablesView>('split');
  protected readonly sort = signal<OpenTablesSort>('wait');
  protected readonly showFreeTables = signal(false);
  protected readonly selectedId = signal<string | null>(null);

  protected readonly large = computed(() => this.viewportWidth() >= LARGE_BREAKPOINT_PX);

  /**
   * The view actually rendered.
   *
   * Narrowing below the breakpoint while in `split` falls back to `list` **without forgetting the choice** — the
   * split view comes back when the window widens again, which is what makes a resize non-destructive.
   */
  protected readonly view = computed<OpenTablesView>(() => {
    const chosen = this.chosenView();
    return !this.large() && chosen === 'split' ? 'list' : chosen;
  });

  protected readonly split = computed(() => this.large() && this.view() === 'split');
  protected readonly showMap = computed(() => this.view() === 'split' || this.view() === 'map');
  protected readonly showList = computed(() => this.view() === 'split' || this.view() === 'list');

  protected readonly viewChips = computed<{key: OpenTablesView; label: string}[]>(() => this.large()
    ? [{key: 'split', label: 'Geteilt'}, {key: 'map', label: 'Karte'}, {key: 'list', label: 'Liste'}]
    : [{key: 'map', label: 'Karte'}, {key: 'list', label: 'Liste'}]);

  /** `9 offen · 345,90 € offen · 00:45` — the handover's own sub-headline. */
  protected readonly headline = computed(() =>
    `${this.store.openTables().length} offen · ${fmtEur(this.store.openTotal())} offen · ` +
    `${fmtClock(this.store.clock())}`);

  protected readonly emptyText = computed(() =>
    this.store.loading() ? 'Offene Tische werden geladen …' : 'Zurzeit sind keine Tische offen.');

  /**
   * The widest the list may currently be: its hard ceiling, further capped so the map keeps
   * {@link MIN_MAP_WIDTH_PX}. Never below {@link LIST_MIN_WIDTH_PX} — on a degenerately narrow split the list
   * is simply pinned at its minimum rather than being handed an empty range.
   */
  protected readonly listMaxWidth = computed(() => {
    const available = this.contentWidth();
    // Before the first measurement (and wherever ResizeObserver is unavailable) fall back to the hard ceiling.
    if (available <= 0) return LIST_MAX_WIDTH_PX;
    return Math.max(LIST_MIN_WIDTH_PX, Math.min(LIST_MAX_WIDTH_PX, Math.round(available) - MIN_MAP_WIDTH_PX));
  });

  /** The width actually rendered: the user's request, clamped into the currently available range. */
  protected readonly listWidth = computed(() =>
    clampPanelWidth(this.requestedListWidth(), LIST_MIN_WIDTH_PX, this.listMaxWidth()));

  constructor() {
    const permissions = inject(AuthenticationService).userValue?.permissions ?? [];
    this.canChangeLimit = permissions.includes(Roles.ShardAdmin) || permissions.includes(Roles.CompanyOwner)
      || permissions.includes(Roles.PlatformOwner);

    this.restorePreferences();

    const onResize = () => this.viewportWidth.set(window.innerWidth);
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));

    // Effects run after the view is created, so the query is resolved by the time this first runs.
    effect(onCleanup => {
      const element = this.content()?.nativeElement;
      if (!element || typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(entries => this.contentWidth.set(entries[0].contentRect.width));
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });
  }

  protected setView(view: OpenTablesView): void {
    this.chosenView.set(view);
    this.persist(VIEW_STORAGE_KEY, view);
  }

  protected setSort(sort: OpenTablesSort): void {
    this.sort.set(sort);
    this.persist(SORT_STORAGE_KEY, sort);
  }

  /**
   * Flips the free-table layer. Raised by the map's own camera-cluster toggle rather than by a header control —
   * but the *state* stays here, because it is a persisted preference that has to survive the map being unmounted
   * in the list-only view.
   */
  protected toggleFreeTables(): void {
    this.showFreeTables.update(value => !value);
    this.persist(FREE_TABLES_STORAGE_KEY, String(this.showFreeTables()));
  }

  /**
   * Remembers the width the user settled on. Called from the handle's `committed` output — once per drag and
   * once per keyboard nudge — rather than from every pointer move, so a drag is one storage write.
   */
  protected persistListWidth(width: number): void {
    this.persist(LIST_WIDTH_STORAGE_KEY, String(width));
  }

  /** Clicking the already-selected card deselects it — the handover's toggle semantics. */
  protected toggleSelection(id: string): void {
    this.selectedId.update(current => current === id ? null : id);
  }

  protected clearSelection(): void {
    this.selectedId.set(null);
  }

  /**
   * A marker press: select, then reveal the matching card **if it is clipped**.
   *
   * The reveal is deferred to after the next render because selecting *expands* the card to all its items, which
   * changes its height and therefore whether it is clipped at all — measuring before that re-render would scroll
   * against stale geometry (the handover does the same thing in `componentDidUpdate`).
   */
  protected onMapSelected(id: string): void {
    const next = this.selectedId() === id ? null : id;
    this.selectedId.set(next);
    if (next === null) return;
    afterNextRender(() => this.list()?.revealCard(next), {injector: this.injector});
  }

  protected refresh(): void {
    this.store.refreshGeometry();
    this.store.refreshConfiguration();
  }

  // region Card actions (#204)

  /**
   * Copies the **booking reference** id — the identity support asks for, and the one the card is keyed on (the
   * table's own id is not what identifies this sitting).
   */
  protected copyReferenceId(row: OpenTable): void {
    this.clipboard.copy(row.id);
    this.alerts.createAndShow('Information', 'ID wurde kopiert.', AlertLevel.info);
  }

  /**
   * Opens the limit dialog.
   *
   * The role is re-checked here even though the menu item is not rendered without it — defence in depth against a
   * future caller (a keyboard shortcut, a story, a test) reaching the handler around the template.
   */
  protected openLimitDialog(row: OpenTable): void {
    if (!this.canChangeLimit) return;
    this.limitTarget.set(row);
  }

  /**
   * The save succeeded: apply the new value to the live rows at once and say so.
   *
   * Optimistic on purpose — the authoritative value arrives on the next SignalR push, which for a quiet table can
   * be minutes away, and until then the card would still be showing the number that was just replaced. See
   * {@link OpenTablesStore.applySpendingLimit}.
   */
  protected onLimitSaved(limit: number | null): void {
    const target = this.limitTarget();
    if (!target) return;
    this.store.applySpendingLimit(target.id, limit);
    this.limitTarget.set(null);
    this.alerts.createAndShow('Gespeichert', 'Das Ausgabenlimit wurde aktualisiert.', AlertLevel.success, 2000);
  }

  // endregion

  /**
   * Restores the persisted view/sort preferences.
   *
   * `getItem` is *not* consent-trapped, so reading is unconditional; writing goes through
   * {@link CookieService.setLocalStorage}, which is the only permitted path (a bare `localStorage.setItem` throws
   * by design — see the privacy guard in `CookieService`).
   */
  private restorePreferences(): void {
    const view = localStorage.getItem(VIEW_STORAGE_KEY);
    if (view === 'split' || view === 'map' || view === 'list') this.chosenView.set(view);

    const sort = localStorage.getItem(SORT_STORAGE_KEY);
    if (sort === 'wait' || sort === 'name') this.sort.set(sort);

    this.showFreeTables.set(localStorage.getItem(FREE_TABLES_STORAGE_KEY) === 'true');
  }

  /**
   * Persists one preference through the consent gate. A refused category simply means it is not remembered
   * (`CookieService` drops the write and warns once); a storage backend that is unavailable altogether — Safari
   * private mode, a quota-exhausted profile — must not take the screen down with it either, hence the catch.
   */
  private persist(key: string, value: string): void {
    try {
      this.cookies.setLocalStorage(CookieType.Functional, key, value);
    } catch {
      // Best effort: the preference is simply forgotten on reload.
    }
  }
}
